/**
 * Settings Service
 * Handles fetching public application settings
 */

import { API_ENDPOINTS } from '../config/api';

export interface RegistrationSettings {
  /** Master switch to enable/disable all registration */
  enabled: boolean;
  individualEnabled: boolean;
  companyEnabled: boolean;
}

export interface DashboardSettings {
  showPricing: boolean;
  showDmvConnections: boolean;
  homepageSubtitle: string;
  coverageMapSubtitle: string;
}

export interface NavigationSettings {
  nmvtisRecordEnabled: boolean;
  theftLienCheckEnabled: boolean;
  motorVehicleRecordEnabled: boolean;
  vinDecoderEnabled: boolean;
}

export interface SettingValue {
  value: string;
  type: string;
}

export interface PublicSettings {
  settings: Record<string, SettingValue>;
}

export interface PolicyContent {
  type: string;
  title: string;
  content: string;
  contentType: string;
  lastUpdated: string;
}

/**
 * Fetch registration settings from the API
 * Returns default values if the request fails
 */
export const getRegistrationSettings = async (): Promise<RegistrationSettings> => {
  try {
    const response = await fetch(API_ENDPOINTS.SETTINGS.REGISTRATION, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch registration settings, using defaults');
      return {
        enabled: true,
        individualEnabled: true,
        companyEnabled: true,
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching registration settings:', error);
    // Return defaults if API fails
    return {
      enabled: true,
      individualEnabled: true,
      companyEnabled: true,
    };
  }
};

/**
 * Fetch all public settings from the API
 */
export const getPublicSettings = async (): Promise<PublicSettings> => {
  try {
    const response = await fetch(API_ENDPOINTS.SETTINGS.PUBLIC, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch public settings');
      return { settings: {} };
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching public settings:', error);
    return { settings: {} };
  }
};

/**
 * Fetch policy content (Terms of Service or Privacy Policy) from the API
 * Returns null if not found or if the request fails
 */
export const getPolicyContent = async (type: 'terms' | 'privacy'): Promise<PolicyContent | null> => {
  try {
    const response = await fetch(API_ENDPOINTS.SETTINGS.POLICY(type), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`Policy content not found for type: ${type}`);
        return null;
      }
      console.error('Failed to fetch policy content');
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching policy content:', error);
    return null;
  }
};

/**
 * Fetch dashboard settings from the API
 * Returns default values if the request fails
 */
export const getDashboardSettings = async (): Promise<DashboardSettings> => {
  try {
    const response = await fetch(API_ENDPOINTS.SETTINGS.DASHBOARD, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch dashboard settings, using defaults');
      return {
        showPricing: true,
        showDmvConnections: true,
        homepageSubtitle: '',
        coverageMapSubtitle: '',
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching dashboard settings:', error);
    // Return defaults if API fails
    return {
      showPricing: true,
      showDmvConnections: true,
      homepageSubtitle: '',
      coverageMapSubtitle: '',
    };
  }
};

/**
 * Fetch navigation settings from the API
 * Controls visibility of menu items in the left navigation
 * Returns default values (all enabled) if the request fails
 */
export const getNavigationSettings = async (): Promise<NavigationSettings> => {
  try {
    const response = await fetch(API_ENDPOINTS.SETTINGS.NAVIGATION, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch navigation settings, using defaults');
      return {
        nmvtisRecordEnabled: true,
        theftLienCheckEnabled: true,
        motorVehicleRecordEnabled: true,
        vinDecoderEnabled: true,
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching navigation settings:', error);
    // Return defaults if API fails (all menu items visible)
    return {
      nmvtisRecordEnabled: true,
      theftLienCheckEnabled: true,
      motorVehicleRecordEnabled: true,
      vinDecoderEnabled: true,
    };
  }
};
